import { observer } from "mobx-react";
import { Component, type SyntheticEvent } from "react";

import { action, computed, makeObservable, observable, runInAction, when } from "mobx";
import { ImageUploader } from "../ui/atoms/image-uploader";
import {
    DefaultButton,
    Dropdown,
    type IDropdownOption,
    type IDropdownProps,
    Label,
    MessageBar,
    MessageBarType,
    Stack,
    TextField,
    Toggle,
} from "@fluentui/react";
import { type PublicSetting, type PublicSettingUpdate, type Setting, Mode } from "../api";
import { PrimaryButtonValidation } from "../ui/atoms/primary-button-validation";
import { Section } from "../ui/atoms/section";
import { ListCommandButtonsSites } from "../ui/organisms/list-command-buttons-sites";
import { ListSites } from "../ui/molecules/list-sites";
import { LoadingState } from "../utils/loading-state";
import { createUuid, type UUID } from "../utils/uuid";
import { doubleBindNullableString } from "../utils/double-bind";
import { ListVehicleTypes } from "../ui/list-vehicle-types";
import { ListCommandButtonsVehicleTypes } from "../ui/organisms/list-command-buttons-vehicle-types";
import { store } from "../store";
import { FusionLayout } from "@/parts/fusion-layout";

enum PageGeneralSettingsFeatures {
    UPDATE_LOGO = "update logo",
    SUBMITTING_SETTINGS = "submitting settings",
}

@observer
export class PageGeneralSettings extends Component {
    @observable private accessor settingId: UUID | undefined;

    private validationId = createUuid();

    private imageBytes: Uint8Array | undefined;
    private siteListStateId = createUuid();
    private vehicleTypeListStateId = createUuid();
    private readonly loadingFeatures = new LoadingState<PageGeneralSettingsFeatures>();

    constructor(props: {}) {
        super(props);
        makeObservable(this);
    }

    async componentDidMount(): Promise<void> {
        const [setting] = await store.repositorySettings.byQueryAsync();
        runInAction(() => {
            this.settingId = setting.id;
        });

        when(
            () => this.publicSetting !== undefined,
            () => {
                store.repositoryPublicSettings.validation.update.initializeModel(
                    this.validationId,
                    this.publicSettingUpdate,
                    this.publicSetting!.id,
                );
            },
        );
    }

    @computed private get publicSetting(): PublicSetting | undefined {
        if (store.servicePublicSettings.publicSetting === undefined) {
            return;
        }
        return store.repositoryPublicSettings.byId(store.servicePublicSettings.publicSetting.id);
    }

    @computed private get publicSettingUpdate(): PublicSettingUpdate {
        return this.publicSetting!;
    }

    @computed private get setting(): Setting | undefined {
        if (this.settingId === undefined) {
            return;
        }
        return store.repositorySettings.byId(this.settingId);
    }

    private triggerValidation(): void {
        store.repositoryPublicSettings.validation.update.updateModel(
            this.validationId,
            this.publicSettingUpdate,
        );
    }

    @action.bound private setImageBytes(bytes?: Uint8Array): void {
        this.imageBytes = bytes;
    }

    @action.bound
    private async updateLogo(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const publicSetting = await store.repositoryPublicSettings.update(
            this.publicSetting!.id,
            this.publicSettingUpdate,
            this.imageBytes,
        );
        this.reloadLogo(publicSetting.logoId);
    }

    @action.bound
    private async updateInstanceName(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        await store.repositoryPublicSettings.update(
            this.publicSetting!.id,
            this.publicSettingUpdate,
        );
    }

    @action.bound
    private reloadLogo(logoId: string | null | undefined): void {
        const logo = store.serviceFile.getFileUri(logoId);
        store.serviceSidebar.setLogo(logo, this.publicSetting?.mode);
    }

    @action.bound
    private async deleteLogo(): Promise<void> {
        const logoId = this.publicSetting?.logoId;
        if (logoId === undefined || logoId === null) {
            return;
        }
        await store.serviceFile.deleteFile(logoId);
        store.serviceSidebar.setLogo(undefined);
    }

    @action.bound
    private setDemoDataGeneration(generateDemoData?: boolean): void {
        if (generateDemoData === undefined || !this.settingId) {
            return;
        }
        store.repositorySettings.update(this.settingId, {
            generateDemoData,
        });
    }

    @action.bound
    private setMode(mode?: Mode): void {
        if (mode === undefined || !this.publicSetting) {
            return;
        }
        store.repositoryPublicSettings.update(this.publicSetting.id, {
            mode,
        });
    }

    public render(): JSX.Element {
        const onRenderLabel = (props?: IDropdownProps): JSX.Element => {
            return (
                <Stack tokens={{ padding: "10px 0px" }}>
                    <Label>{props?.label}</Label>
                    <MessageBar messageBarType={MessageBarType.severeWarning}>
                        {store.i18n.t("page.generalSettings.mode.warning")}
                    </MessageBar>
                </Stack>
            );
        };
        const modeOptions: IDropdownOption[] = [
            {
                key: Mode.ELOFLEET,
                text: store.i18n.t("page.generalSettings.mode.elofleet"),
            },
            {
                key: Mode.ELOFUSION,
                text: store.i18n.t("page.generalSettings.mode.elofusion"),
            },
            {
                key: Mode.ELOSHIELD,
                text: store.i18n.t("page.generalSettings.mode.eloshield"),
            },
        ];
        // wait until setting is loaded from backend
        // otherwise the initial image won't be set
        if (this.publicSetting === undefined) {
            return (
                <FusionLayout breadcrumb={store.i18n.t("page.generalSettings.title")} classic>
                    <></>
                </FusionLayout>
            );
        }
        return (
            <FusionLayout breadcrumb={store.i18n.t("page.generalSettings.title")} classic>
                <div className="flex flex-wrap gap-6">
                    <Section withPadding title={store.i18n.t("page.generalSettings.logoUpload")}>
                        <form
                            onSubmit={(evt) =>
                                this.loadingFeatures.wrap(
                                    PageGeneralSettingsFeatures.UPDATE_LOGO,
                                    this.updateLogo(evt),
                                )
                            }
                        >
                            <Stack
                                horizontal
                                horizontalAlign="space-between"
                                tokens={{
                                    childrenGap: "2em",
                                }}
                            >
                                <ImageUploader
                                    onImageChanged={this.setImageBytes}
                                    initialImage={this.publicSetting?.logoId}
                                    placeholderIcon="FileImage"
                                />
                            </Stack>
                            <Stack
                                horizontal
                                horizontalAlign="start"
                                tokens={{
                                    childrenGap: "2em",
                                }}
                            >
                                <PrimaryButtonValidation
                                    buttonDisabled={this.loadingFeatures.isLoading(
                                        PageGeneralSettingsFeatures.UPDATE_LOGO,
                                    )}
                                    validationLoading={this.loadingFeatures.isLoading(
                                        PageGeneralSettingsFeatures.UPDATE_LOGO,
                                    )}
                                    text={store.i18n.t("page.generalSettings.logoSubmit.text")}
                                />
                                <DefaultButton
                                    text="Delete logo"
                                    disabled={this.loadingFeatures.isLoading(
                                        PageGeneralSettingsFeatures.UPDATE_LOGO,
                                    )}
                                    onClick={() =>
                                        this.loadingFeatures.wrap(
                                            PageGeneralSettingsFeatures.UPDATE_LOGO,
                                            this.deleteLogo(),
                                        )
                                    }
                                />
                            </Stack>
                        </form>
                    </Section>
                    <Section
                        withPadding
                        title={store.i18n.t("page.systemWide.site.title")}
                        icon="CityNext2"
                    >
                        <ListCommandButtonsSites listStateId={this.siteListStateId} canDelete />
                        <ListSites listStateId={this.siteListStateId} />
                    </Section>

                    <Section withPadding title={store.i18n.t("page.systemWide.vehicleTypes.title")}>
                        <ListCommandButtonsVehicleTypes
                            listStateId={this.vehicleTypeListStateId}
                            canDelete
                        />
                        <ListVehicleTypes listStateId={this.vehicleTypeListStateId} />
                    </Section>

                    <Section
                        withPadding
                        title={store.i18n.t("page.generalSettings.settings.title")}
                    >
                        {this.publicSetting && (
                            <form
                                onSubmit={(evt) =>
                                    this.loadingFeatures.wrap(
                                        PageGeneralSettingsFeatures.SUBMITTING_SETTINGS,
                                        this.updateInstanceName(evt),
                                    )
                                }
                            >
                                <Stack
                                    tokens={{
                                        childrenGap: "1em",
                                    }}
                                >
                                    <TextField
                                        label={store.i18n.t(
                                            "page.generalSettings.instanceName.label",
                                        )}
                                        {...doubleBindNullableString(
                                            this.publicSettingUpdate,
                                            "instanceName",
                                            () => this.triggerValidation(),
                                        )}
                                        required
                                        errorMessage={store.i18n.formatFieldValidationState(
                                            store.repositoryPublicSettings.validation.update.getFieldValidationState(
                                                this.validationId,
                                                "instanceName",
                                            ),
                                        )}
                                    />
                                    <PrimaryButtonValidation
                                        buttonDisabled={this.loadingFeatures.isLoading(
                                            PageGeneralSettingsFeatures.SUBMITTING_SETTINGS,
                                        )}
                                        validationLoading={this.loadingFeatures.isLoading(
                                            PageGeneralSettingsFeatures.SUBMITTING_SETTINGS,
                                        )}
                                        text={store.i18n.t(
                                            "page.generalSettings.settingsSubmit.text",
                                        )}
                                    />
                                </Stack>
                            </form>
                        )}
                    </Section>
                    <Section
                        withPadding
                        title={store.i18n.t("page.generalSettings.featureFlags.title")}
                    >
                        {this.setting && (
                            <>
                                <Toggle
                                    label={store.i18n.t(
                                        "page.generalSettings.featureFlags.generateDemoData",
                                    )}
                                    defaultChecked={this.setting.generateDemoData}
                                    onChange={(_event, checked) =>
                                        this.setDemoDataGeneration(checked)
                                    }
                                />
                                <Toggle
                                    label={store.i18n.t(
                                        "page.generalSettings.featureFlags.agvSection",
                                    )}
                                    defaultChecked={store.serviceAgv.enabled}
                                    onChange={(_event, checked) => {
                                        if (checked != null) {
                                            store.serviceAgv.setEnabled(checked);
                                        }
                                    }}
                                />
                                <Toggle
                                    label={store.i18n.t(
                                        "page.generalSettings.featureFlags.robotsSection",
                                    )}
                                    defaultChecked={store.serviceRobots.enabled}
                                    onChange={(_event, checked) => {
                                        if (checked != null) {
                                            store.serviceRobots.setEnabled(checked);
                                        }
                                    }}
                                />
                                <Dropdown
                                    label={store.i18n.t("page.generalSettings.mode")}
                                    onRenderLabel={onRenderLabel}
                                    defaultSelectedKey={this.publicSetting.mode}
                                    options={modeOptions}
                                    styles={{ root: { maxWidth: 400 } }}
                                    onChange={(_e, item?: IDropdownOption) => {
                                        if (!item) {
                                            return;
                                        }
                                        this.setMode(item.key as Mode);
                                    }}
                                />
                            </>
                        )}
                    </Section>
                </div>
            </FusionLayout>
        );
    }
}
